import React from 'react';
import { SxStyle } from '../../../../types';
import { useStringsContext } from '../../../../contexts/strings';
import { EditableBottomBarItem } from '../types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { bottomBarIconsMap } from '../icons';

const styles: SxStyle = {
  headContainer: {
    display: 'flex',
    flex: 1,
    height: '48px',
    alignItems: 'center',
    borderRadius: 1,
    backgroundColor: '#F5F5F5',
    padding: '1px',
  },
  dragButton: {
    minWidth: '40px',
    height: '100%',
    borderRadius: 0,
  },
  dragging: {
    border: '1px dashed #b4bbc8',
    backgroundColor: '#F5F5F5',
    padding: 0,
  },
  expanded: {
    height: 'auto',
  },
  invalid: {
    border: '1px solid',
  },
  numContainer: {
    width: '48px',
    height: '48px',
    backgroundColor: '#003168',
    color: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 1,
  },
  numDragging: {
    opacity: 0,
  },
  iconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    display: 'inline-flex',
    width: '38px',
    height: '38px',
    padding: '8px',
  },
  link: {
    textDecoration: 'underline',
  },
  text: {
    flex: 1,
  },
};

interface Props {
  bottomBarItem: EditableBottomBarItem;
  position?: number;
  listeners?: any;
  isDragging?: boolean;
  isExpanded: boolean;
  onExpanded?: (expanded: boolean) => void;
  isValid: boolean;
}

const AppMenuItemHeader = ({
  bottomBarItem,
  position,
  listeners,
  isDragging,
  isExpanded,
  onExpanded,
  isValid = true,
}: Props) => {
  const { strings } = useStringsContext();

  const numStyling = {
    ...styles.numContainer,
    ...(isDragging ? styles.numDragging : {}),
  };

  const headerStyling = {
    ...styles.headContainer,
    ...(isDragging ? styles.dragging : {}),
    ...(!isValid ? styles.invalid : {}),
  };

  const MenuItemIconComponent = bottomBarIconsMap[bottomBarItem.icon]
    ? bottomBarIconsMap[bottomBarItem.icon]
    : null;

  return (
    <Stack spacing={1} direction="row">
      <Box sx={numStyling}>
        <Typography>{position}</Typography>
      </Box>
      <Stack
        sx={{ ...headerStyling, ...(!isValid ? { borderColor: 'error.main' } : {}) }}
        direction="row"
        spacing={2}
      >
        <Box sx={styles.iconContainer}>
          {MenuItemIconComponent && <MenuItemIconComponent style={{ color: 'black' }} />}
        </Box>
        <Typography variant="body1" sx={styles.text}>
          {bottomBarItem.text}
        </Typography>
        {bottomBarItem.editable && (
          <Link
            sx={styles.link}
            component="button"
            variant="caption"
            onClick={() => onExpanded?.(!isExpanded)}
          >
            {isExpanded ? strings.appMenu.menuItemClose : strings.appMenu.menuItemEdit}
          </Link>
        )}
        {bottomBarItem.draggable && (
          <IconButton disabled={isExpanded} sx={styles.dragButton} {...listeners}>
            <DragIndicatorIcon />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

export default AppMenuItemHeader;
